import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useSEO } from "@/hooks/useSEO";
import { LogOut, FileText, Wrench, Mail, RefreshCw, ExternalLink } from "lucide-react";

const TABS = [
  { key: "quote", label: "Quotes", icon: FileText },
  { key: "part_request", label: "Part Requests", icon: Wrench },
  { key: "contact", label: "Messages", icon: Mail },
];

const STATUS_STYLE = {
  new: "text-strike border-strike",
  reviewed: "text-offwhite border-offwhite/40",
  closed: "text-mutedgray border-mutedgray/40",
};

export default function AdminDashboard() {
  useSEO({ title: "Admin Dashboard | BlackSilex", noindex: true });
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("quote");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (ready && !user) navigate("/admin/login"); }, [ready, user, navigate]);

  const loadStats = useCallback(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const loadItems = useCallback((t) => {
    setLoading(true);
    api.get(`/admin/submissions?type=${t}`).then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (user) { loadStats(); loadItems(tab); } }, [user, tab, loadStats, loadItems]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/submissions/${id}`, { status });
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    if (selected?.id === id) setSelected({ ...selected, status });
    loadStats();
  };

  if (!ready || !user) return <div className="min-h-screen bg-coal" />;

  return (
    <div className="min-h-screen bg-coal grain text-offwhite" data-testid="admin-dashboard">
      <header className="border-b border-offwhite/10 sticky top-0 bg-coal/80 backdrop-blur-xl z-50">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="font-mono-tech text-xs text-mutedgray hidden md:inline">{user.email}</span>
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="cta-ghost inline-flex items-center gap-2 px-5 py-3 font-mono-tech text-xs uppercase tracking-[0.2em]" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between">
          <div>
            <p className="tech-label text-strike">Control Room</p>
            <h1 className="font-display font-black uppercase tracking-tight text-4xl md:text-5xl mt-3">Submissions</h1>
          </div>
          <button onClick={() => { loadStats(); loadItems(tab); }} className="text-mutedgray hover:text-strike transition-colors" data-testid="admin-refresh" aria-label="Refresh">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-offwhite/10 border border-offwhite/10 mt-10">
          {[
            { l: "Total", v: stats?.total },
            { l: "New", v: stats?.new, hot: true },
            { l: "Quotes", v: stats?.quotes },
            { l: "Part Requests", v: stats?.part_requests },
            { l: "Messages", v: stats?.contacts },
          ].map((s) => (
            <div key={s.l} className="bg-coal p-6 lg:p-8" data-testid={`stat-${s.l.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="tech-label text-mutedgray">{s.l}</p>
              <p className={`font-display text-4xl lg:text-5xl font-black mt-4 ${s.hot ? "text-strike" : "text-offwhite"}`}>{s.v ?? "—"}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-12 border-b border-offwhite/10 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setSelected(null); }}
                className={`inline-flex items-center gap-2 px-6 py-4 whitespace-nowrap font-mono-tech text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === t.key ? "border-strike text-offwhite" : "border-transparent text-mutedgray hover:text-offwhite"}`}
                data-testid={`tab-${t.key}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="mt-8">
          {loading ? (
            <p className="font-mono-tech text-mutedgray text-sm py-12">Loading…</p>
          ) : items.length === 0 ? (
            <p className="font-mono-tech text-mutedgray text-sm py-12" data-testid="empty-state">No submissions yet.</p>
          ) : (
            <div className="border border-offwhite/10 divide-y divide-offwhite/10">
              {items.map((it) => (
                <div key={it.id} className="grid grid-cols-12 gap-4 items-center p-5 hover:bg-nearblack transition-colors" data-testid={`row-${it.id}`}>
                  <div className="col-span-12 md:col-span-3">
                    <p className="font-display text-lg font-bold text-offwhite">{it.name}</p>
                    <p className="font-mono-tech text-xs text-mutedgray">{it.email}</p>
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <p className="text-sm text-offwhite/80">{it.company || it.machine_model || it.part_number || "—"}</p>
                    <p className="font-mono-tech text-[0.65rem] text-mutedgray">{(it.created_at || "").slice(0, 10)}</p>
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <span className={`font-mono-tech text-[0.6rem] uppercase tracking-[0.15em] border px-3 py-1 ${STATUS_STYLE[it.status] || STATUS_STYLE.new}`}>{it.status}</span>
                  </div>
                  <div className="col-span-3 md:col-span-4 flex justify-end gap-2">
                    <button onClick={() => setSelected(it)} className="cta-ghost px-4 py-2 font-mono-tech text-[0.65rem] uppercase tracking-[0.15em]" data-testid={`view-${it.id}`}>View</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-[200] flex justify-end" data-testid="detail-drawer">
          <div className="absolute inset-0 bg-coal/70 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-xl bg-nearblack border-l border-offwhite/10 h-full overflow-y-auto p-8 lg:p-10">
            <div className="flex items-center justify-between">
              <p className="tech-label text-strike">{tab.replace("_", " ")}</p>
              <button onClick={() => setSelected(null)} className="text-mutedgray hover:text-strike font-mono-tech text-xs" data-testid="drawer-close">CLOSE ✕</button>
            </div>
            <h2 className="font-display text-3xl font-black text-offwhite mt-4">{selected.name}</h2>
            <a href={`mailto:${selected.email}`} className="font-mono-tech text-sm text-strike link-underline">{selected.email}</a>

            <div className="mt-8 space-y-4">
              {Object.entries(selected).filter(([k]) => !["id", "type", "status", "name", "email", "files", "created_at"].includes(k)).map(([k, v]) => (
                v ? (
                  <div key={k} className="border-b border-offwhite/10 pb-3">
                    <p className="tech-label text-mutedgray">{k.replace(/_/g, " ")}</p>
                    <p className="text-offwhite/90 mt-2 whitespace-pre-wrap">{String(v)}</p>
                  </div>
                ) : null
              ))}
              {selected.files?.length > 0 && (
                <div className="border-b border-offwhite/10 pb-3">
                  <p className="tech-label text-mutedgray">Attachments</p>
                  <ul className="mt-2 space-y-2">
                    {selected.files.map((f) => (
                      <li key={f.id}>
                        <a href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-strike font-mono-tech text-xs link-underline" data-testid={`file-link-${f.id}`}>
                          <ExternalLink className="w-3.5 h-3.5" /> {f.original_filename}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-8">
              <p className="tech-label text-mutedgray mb-3">Update Status</p>
              <div className="flex gap-2">
                {["new", "reviewed", "closed"].map((s) => (
                  <button key={s} onClick={() => updateStatus(selected.id, s)}
                    className={`px-4 py-2 font-mono-tech text-[0.65rem] uppercase tracking-[0.15em] border transition-colors ${selected.status === s ? "bg-strike text-coal border-strike" : "border-steel text-mutedgray hover:text-offwhite"}`}
                    data-testid={`status-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
